import React, { useEffect } from 'react';
import { ArrowUpRight, Mail, Send } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';

const content = {
  RU: {
    kicker: 'ОБ АРХИВЕ',
    title: 'Проект, созданный для сохранения предметной истории',
    lead: 'MaximGun.ru — независимый некоммерческий архив для коллекционеров, историков, музейных сотрудников, реконструкторов и исследователей.',
    sections: [
      ['Задача', 'Систематизировать сведения о сохранившихся пулемётах системы Максима, станках, деталях, модификациях и технических особенностях. Серийные номера, фотографии и наблюдения связываются между собой, чтобы каждый вывод можно было проверить.'],
      ['Материалы', 'Изображения и сведения размещаются исключительно в исследовательских и справочных целях. Они поступают из открытых источников, музейных собраний и от участников проекта. Источник и авторство указываются там, где они известны.'],
      ['Исправления и авторские права', 'Если вы обнаружили ошибку или являетесь правообладателем материала, напишите нам. Мы готовы уточнить подпись, добавить авторство, заменить или удалить публикацию.'],
      ['Участие', 'Особенно ценны фотографии маркировок, общие виды предметов, сведения о происхождении и ссылки на первоисточники. Любое предположение отделяется от подтверждённого факта.'],
    ],
    contact: 'Связаться с автором проекта',
    friendsKicker: 'БЛАГОДАРНОСТЬ',
    friendsTitle: 'Друзья проекта',
    friendsLead: 'Около 90% материалов архива появились благодаря этим людям и организациям.',
    friends: [
      {
        name: 'Заболотный Василий Александрович',
        org: 'Музей отечественной военной истории',
        url: 'https://www.kskdivniy.ru/museum/',
        text: 'Передал уникальный архив фотографий и предоставил фондовые образцы для фотосессии.',
      },
      {
        name: 'Комаров Владимир Анатольевич',
        text: 'Передал обширный архив фотографий пулемётов и лентоприёмников.',
      },
      {
        name: 'особыйотдел',
        org: 'osob.store',
        url: 'https://osob.store/',
        text: 'Предоставил право использовать свои материалы.',
      },
    ],
  },
  EN: {
    kicker: 'ABOUT THE ARCHIVE',
    title: 'A project created to preserve material history',
    lead: 'MaximGun.ru is an independent, non-commercial archive for collectors, historians, museum staff, reenactors and researchers.',
    sections: [
      ['Purpose', 'To organise information about surviving Maxim guns, mounts, parts, modifications and technical features. Serial numbers, photographs and observations are linked so that every conclusion can be checked.'],
      ['Material', 'Images and information are published solely for research and reference. They come from open sources, museum collections and project contributors. Sources and authorship are credited whenever known.'],
      ['Corrections and rights', 'If you find an error or own the rights to any material, please contact us. We can correct the caption, add credit, replace or remove the publication.'],
      ['Contributing', 'Photographs of markings, full object views, provenance and links to primary sources are especially valuable. Hypotheses are kept distinct from verified facts.'],
    ],
    contact: 'Contact the project author',
    friendsKicker: 'ACKNOWLEDGEMENTS',
    friendsTitle: 'Friends of the project',
    friendsLead: 'Around 90% of the archive exists thanks to these people and organisations.',
    friends: [
      {
        name: 'Vasily A. Zabolotny',
        org: 'Museum of Russian Military History',
        url: 'https://www.kskdivniy.ru/museum/',
        text: 'Donated a unique photographic archive and provided museum collection items for photography.',
      },
      {
        name: 'Vladimir A. Komarov',
        text: 'Donated an extensive archive of photographs of machine guns and feed blocks.',
      },
      {
        name: 'особыйотдел',
        org: 'osob.store',
        url: 'https://osob.store/',
        text: 'Granted the right to use their material.',
      },
    ],
  },
};

export default function AboutPage() {
  const { lang } = useLanguage();
  const page = content[lang] || content.RU;
  const { hash } = useLocation();

  useEffect(() => {
    if (hash !== '#friends') return undefined;
    // Wait for the scroll manager's reset to top before jumping to the section.
    const timer = window.setTimeout(() => {
      document.getElementById('friends')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [hash]);

  return (
    <main className="editorial-page page-width">
      <header className="editorial-page__header">
        <p className="section-kicker">{page.kicker}</p>
        <h1>{page.title}</h1>
        <p>{page.lead}</p>
      </header>

      <section className="project-friends" id="friends" aria-labelledby="friends-title">
        <div className="project-friends__intro">
          <p className="section-kicker">{page.friendsKicker}</p>
          <h2 id="friends-title">{page.friendsTitle}</h2>
          <p>{page.friendsLead}</p>
        </div>
        <ul className="project-friends__list">
          {page.friends.map((friend) => (
            <li key={friend.name}>
              <h3>{friend.name}</h3>
              {friend.org && (friend.url
                ? <a href={friend.url} target="_blank" rel="noopener noreferrer">{friend.org}<ArrowUpRight size={14} /></a>
                : <span>{friend.org}</span>)}
              <p>{friend.text}</p>
            </li>
          ))}
        </ul>
      </section>

      <div className="editorial-page__body">
        <div className="editorial-sections">
          {page.sections.map(([title, text], index) => (
            <section className="editorial-section" key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><h2>{title}</h2><p>{text}</p></div>
            </section>
          ))}
        </div>

        <aside className="contact-sheet">
          <p className="section-kicker">CONTACT</p>
          <h2>{page.contact}</h2>
          <a href="https://t.me/noglef_qa" target="_blank" rel="noopener noreferrer"><Send size={17} />Telegram · @noglef_qa</a>
          <a href="mailto:i@noglef.ru"><Mail size={17} />i@noglef.ru</a>
        </aside>
      </div>
    </main>
  );
}
